import React, { useState } from 'react';
import '../Assets/AuroraTrendChart.css';

const { getVisibleMonths } = require('./auroraTrendUtils');

/**
 * "1b Aurora trends" — a responsive grid of metric cards, each showing a big
 * YTD figure plus a glowing gradient trend line (smooth bezier area chart) with
 * a labelled dot for every month. Ported from the Dashboard Chart Options design.
 *
 * Props:
 *   metrics  Array<{ title, loss, months }> where months is [ [monthLabel, value], ... ]
 *   accent   Positive-metric palette key (default 'Cyan → Blue'); losses always contrast.
 *   glow     Whether the trend line carries a soft glow (default true).
 *   year     Year shown in the "YTD <year> · %" caption.
 */

const PALETTES = {
    'Cyan → Blue': ['#06c6e8', '#2f6bff'],
    'Violet → Fuchsia': ['#7c5cff', '#e34bd0'],
    'Teal → Lime': ['#0fcf9f', '#7ac412'],
};

const LOSS_PALETTES = {
    quality: { colors: ['#e98299', '#c53c5d'] },
    technical: { colors: ['#74a0e5', '#3676da'] },
    organization: { colors: ['#d9a94e', '#b7791f'] },
};

// SVG viewBox geometry (matches the design's 1b option).
const VB_W = 260;
const VB_H = 118;
const X0 = 22;
const X1 = 238;
const Y_TOP = 30;
const Y_BOT = 92;
const BASELINE = 96;

const round1 = (n) => Math.round(n * 10) / 10;

function normalizeMonths(months) {
    return (months || []).filter(m => (Array.isArray(m) ? m[1] : m.v) != null).map((m) =>
        Array.isArray(m)
            ? { m: m[0], v: Number(m[1]) || 0 }
            : { m: m.m, v: Number(m.v) || 0 }
    );
}

function getLossTone(title) {
    const normalized = String(title || '').toLowerCase();
    if (normalized.includes('technical')) return 'technical';
    if (normalized.includes('organization')) return 'organization';
    return 'quality';
}

function isProductivityMetric(title) {
    return String(title || '').toLowerCase().includes('productivity');
}

function buildCard(metric, colors, glow, year, lossTone, isProductivity) {
    const suffix = lossTone ? `loss-${lossTone}` : isProductivity ? 'productivity' : 'pos';
    const months = normalizeMonths(getVisibleMonths(metric.months, year));
    const vals = months.map((p) => p.v);
    const total = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 'N/A';
    const min = vals.length ? Math.min(...vals) : 0;
    const max = vals.length ? Math.max(...vals) : 0;
    const n = months.length;
    const step = n > 1 ? (X1 - X0) / (n - 1) : 0;
    const yFor = (v) =>
        max === min ? (Y_TOP + Y_BOT) / 2 : round1(Y_BOT - ((v - min) / (max - min)) * (Y_BOT - Y_TOP));

    const points = months.map((p, i) => {
        const x = n > 1 ? round1(X0 + i * step) : (X0 + X1) / 2;
        const y = yFor(p.v);
        const leftPct = round1((x / VB_W) * 100) + '%';
        return {
            ...p,
            x,
            y,
            valStyle: {
                position: 'absolute',
                left: leftPct,
                top: round1(((y - 7) / VB_H) * 100) + '%',
                transform: 'translate(-50%,-100%)',
                fontSize: '10.5px',
                fontWeight: 700,
                color: '#26324b',
                pointerEvents: 'none',
            },
            monStyle: {
                position: 'absolute',
                left: leftPct,
                top: round1((103 / VB_H) * 100) + '%',
                transform: 'translate(-50%,0)',
                fontSize: '9.5px',
                fontWeight: 600,
                color: '#8794ab',
                pointerEvents: 'none',
            },
        };
    });

    let linePath = '';
    let areaPath = '';
    if (n === 1) {
        const y = points[0].y;
        linePath = `M${X0} ${y} L${X1} ${y}`;
        areaPath = `${linePath} L${X1} ${BASELINE} L${X0} ${BASELINE} Z`;
    } else if (n > 1) {
        linePath = `M${points[0].x} ${points[0].y}`;
        for (let i = 1; i < points.length; i++) {
            const a = points[i - 1];
            const p = points[i];
            const mx = round1((a.x + p.x) / 2);
            linePath += ` C ${mx} ${a.y}, ${mx} ${p.y}, ${p.x} ${p.y}`;
        }
        areaPath = `${linePath} L ${points[points.length - 1].x} ${BASELINE} L ${points[0].x} ${BASELINE} Z`;
    }

    return {
        title: metric.title,
        total,
        points,
        linePath,
        areaPath,
        numColor: colors[1],
        dotC: colors[0],
        grad: `url(#atGrad-${suffix})`,
        areaFill: `url(#atArea-${suffix})`,
        filt: glow ? `url(#atGlow-${suffix})` : 'none',
        lossTone,
        isProductivity,
    };
}

function Defs({ pos, productivity, lossPalettes }) {
    const pair = [
        ['pos', pos],
        ['productivity', productivity],
        ...Object.entries(lossPalettes).map(([tone, palette]) => [`loss-${tone}`, palette.colors]),
    ];
    return (
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
            <defs>
                {pair.map(([id, c]) => (
                    <linearGradient key={`g-${id}`} id={`atGrad-${id}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor={c[0]} />
                        <stop offset="1" stopColor={c[1]} />
                    </linearGradient>
                ))}
                {pair.map(([id, c]) => (
                    <linearGradient key={`a-${id}`} id={`atArea-${id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor={c[0]} stopOpacity="0.26" />
                        <stop offset="1" stopColor={c[1]} stopOpacity="0.02" />
                    </linearGradient>
                ))}
                {pair.map(([id, c]) => (
                    <filter key={`f-${id}`} id={`atGlow-${id}`} x="-60%" y="-60%" width="220%" height="220%">
                        <feDropShadow dx="0" dy="0" stdDeviation="3.5" floodColor={c[0]} floodOpacity="0.55" />
                    </filter>
                ))}
            </defs>
        </svg>
    );
}

const CARD_DELAY_STEP = 0.06;

function AuroraCard({ card, ytdCaption, index }) {
    const [active, setActive] = useState(null);
    const baseDelay = index * CARD_DELAY_STEP;
    const cardVariantClass = card.lossTone
        ? ` aurora-card--loss-${card.lossTone}`
        : card.isProductivity ? ' aurora-card--productivity' : '';

    const header = (
        <>
            <div className="aurora-card-title">{card.title}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px' }}>
                <div className="aurora-total" style={{ color: card.numColor }}>{card.total}</div>
                <div className="aurora-ytd">{ytdCaption}</div>
            </div>
        </>
    );

    if (card.points.length === 0) {
        return (
            <div className={`aurora-card${cardVariantClass}`} style={{ animationDelay: `${baseDelay}s` }}>
                {header}
                <div className="aurora-empty">No data for this period</div>
            </div>
        );
    }

    // Keep hover targets from overlapping when there are many months.
    const dx = card.points.length > 1 ? card.points[1].x - card.points[0].x : 40;
    const hitR = Math.max(6, Math.min(14, dx / 2));
    const guide = active != null ? card.points[active] : null;

    return (
        <div className={`aurora-card${cardVariantClass}`} style={{ animationDelay: `${baseDelay}s` }}>
            {header}
            <div style={{ position: 'relative', marginTop: '4px' }}>
                <svg viewBox={`0 0 ${VB_W} ${VB_H}`} style={{ width: '100%', display: 'block', overflow: 'visible' }}>
                    <path
                        className="aurora-area"
                        style={{ animationDelay: `${baseDelay + 0.25}s` }}
                        d={card.areaPath}
                        fill={card.areaFill}
                        stroke="none"
                    />
                    {guide && (
                        <line
                            className="aurora-guide"
                            x1={guide.x}
                            y1="6"
                            x2={guide.x}
                            y2={BASELINE}
                            stroke={card.dotC}
                            strokeWidth="1"
                            strokeDasharray="3 3"
                            opacity="0.5"
                        />
                    )}
                    <path
                        className="aurora-line"
                        style={{ animationDelay: `${baseDelay}s` }}
                        pathLength="100"
                        d={card.linePath}
                        fill="none"
                        stroke={card.grad}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        filter={card.filt}
                    />
                    {card.points.map((p, idx) => {
                        const on = active === idx;
                        return (
                            <g key={`pt-${idx}`}>
                                <title>{`${p.m}: ${p.v}%`}</title>
                                {on && <circle cx={p.x} cy={p.y} r="7.5" fill={card.dotC} opacity="0.16" />}
                                <circle
                                    className="aurora-dot"
                                    style={{ animationDelay: `${baseDelay + 0.3 + idx * 0.08}s` }}
                                    cx={p.x}
                                    cy={p.y}
                                    r={on ? 5 : 3.4}
                                    fill="#ffffff"
                                    stroke={card.dotC}
                                    strokeWidth="2.2"
                                />
                                <circle
                                    className="aurora-hit"
                                    cx={p.x}
                                    cy={p.y}
                                    r={hitR}
                                    fill="transparent"
                                    onMouseEnter={() => setActive(idx)}
                                    onMouseLeave={() => setActive(null)}
                                    onClick={() => setActive((cur) => (cur === idx ? null : idx))}
                                />
                            </g>
                        );
                    })}
                </svg>
                {card.points.map((p, idx) => (
                    <div
                        key={`v-${idx}`}
                        className="aurora-vlabel"
                        style={{
                            ...p.valStyle,
                            animationDelay: `${baseDelay + 0.35 + idx * 0.08}s`,
                            ...(active === idx ? { color: card.numColor } : null),
                        }}
                    >
                        {p.v}
                    </div>
                ))}
                {card.points.map((p, idx) => (
                    <div
                        key={`m-${idx}`}
                        className="aurora-mlabel"
                        style={{
                            ...p.monStyle,
                            animationDelay: `${baseDelay + 0.4 + idx * 0.08}s`,
                            ...(active === idx ? { color: '#16223a', fontWeight: 700 } : null),
                        }}
                    >
                        {p.m}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AuroraTrends({ metrics = [], accent = 'Cyan → Blue', glow = true, year }) {
    const pos = PALETTES[accent] || PALETTES['Cyan → Blue'];
    const ytdCaption = year ? `YTD ${year} · %` : 'YTD · %';

    return (
        <div className="aurora-trends">
            <Defs pos={pos} productivity={PALETTES['Violet → Fuchsia']} lossPalettes={LOSS_PALETTES} />
            <div className="aurora-panel">
                <div className="aurora-grid">
                    {metrics.map((m, i) => {
                        const lossTone = m.loss ? getLossTone(m.title) : null;
                        const isProductivity = !m.loss && isProductivityMetric(m.title);
                        const colors = lossTone
                            ? LOSS_PALETTES[lossTone].colors
                            : isProductivity ? PALETTES['Violet → Fuchsia'] : pos;
                        return (
                            <AuroraCard
                                key={m.title || i}
                                index={i}
                                card={buildCard(m, colors, glow, year, lossTone, isProductivity)}
                                ytdCaption={ytdCaption}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
